/**
 * Symbol Library — Projects v2 Phase 4a (P4a.4).
 *
 * Stroke-based SVGs for floorplan annotations. Each symbol is keyed
 * by `id`, grouped under one of six master-plan §6.2 layers, and
 * uses `stroke="currentColor"` so layer colour drives appearance:
 *   - Networking → sky blue
 *   - Security → rose
 *   - Smart Home → violet
 *   - AV → amber
 *   - Power → orange
 *   - Structured → slate
 *
 * The P4a.8 picker reads SYMBOLS to render its category-grouped
 * panel; the P4a.6 canvas reads via getSymbol() when materialising
 * a placed annotation. Each definition's `defaultCableType` +
 * `defaultCatalogHints` feed P4a.9's cable-run linker.
 *
 * Authoring rule (master plan §4.3): SVG body is the inner content
 * only — paths/circles/rects WITHOUT the wrapping <svg>. The viewer
 * wraps each with a 24×24 viewBox + currentColor stroke at render
 * time. Keeps the strings small (~150 B each) and consistent.
 */

export type SymbolLayer =
  | 'networking'
  | 'security'
  | 'smart_home'
  | 'av'
  | 'power'
  | 'structured';

export interface SymbolDefinition {
  /** Stable id, kebab-case. Persisted on PlanAnnotation.symbolId. */
  id: string;
  /** Short human label for the picker. */
  label: string;
  layer: SymbolLayer;
  /** Inner SVG markup (paths, circles, etc.) — viewer wraps with the
   *  outer <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
   *  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">. */
  svg: string;
  /** Suggested cable-catalog search hint for P4a.9's linker. */
  defaultCableType?: string;
  /** Suggested parts-catalog query hints. */
  defaultCatalogHints?: string[];
  /**
   * Per-symbol stroke colour, overriding the layer accent. Only user-created
   * symbols set this — the built-in catalogue is deliberately layer-coloured
   * so a plan reads by discipline at a glance. Resolve it through
   * `resolveSymbolHex` rather than reading it directly, so every surface
   * (canvas, read-only viewer, PDF export) agrees.
   */
  color?: string;
  /** True for symbols the user created. Drives the edit/delete affordances
   *  in the picker, and nothing else — a custom symbol is a symbol. */
  custom?: boolean;
}

// ---------------------------------------------------------------------------
// Networking (5)
// ---------------------------------------------------------------------------

const NETWORKING: SymbolDefinition[] = [
  {
    id: 'ap-ceiling',
    label: 'Ceiling AP',
    layer: 'networking',
    // Disc with concentric ring + signal arcs (UniFi-style).
    svg: '<circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="7"/><path d="M12 5V3M12 21v-2M5 12H3M21 12h-2"/>',
    defaultCableType: 'Cat6A',
    defaultCatalogHints: ['UniFi U7', 'U6-Pro', 'access point'],
  },
  {
    id: 'ap-wall',
    label: 'Wall AP',
    layer: 'networking',
    // Rectangle with wifi arcs.
    svg: '<rect x="6" y="4" width="12" height="16" rx="2"/><path d="M9 9q3-2 6 0M10 12q2-1 4 0"/>',
    defaultCableType: 'Cat6A',
    defaultCatalogHints: ['wall AP', 'in-wall', 'iw-hd'],
  },
  {
    id: 'switch-8port',
    label: 'Switch 8-port',
    layer: 'networking',
    // Long rectangle + 8 small port slots.
    svg: '<rect x="2" y="9" width="20" height="6" rx="1"/><path d="M5 11v2M7 11v2M9 11v2M11 11v2M13 11v2M15 11v2M17 11v2M19 11v2"/>',
    defaultCableType: 'Cat6A',
    defaultCatalogHints: ['switch', 'PoE switch', 'USW'],
  },
  {
    id: 'patch-panel',
    label: 'Patch Panel',
    layer: 'networking',
    // Wide rectangle with rows of ports.
    svg: '<rect x="2" y="6" width="20" height="12" rx="1"/><path d="M5 10h14M5 14h14"/>',
    defaultCatalogHints: ['patch panel', 'keystone', 'rack mount'],
  },
  {
    id: 'nbn-ntd',
    label: 'NBN NTD',
    layer: 'networking',
    // Box with "N" + signal indicator dots.
    svg: '<rect x="4" y="6" width="16" height="12" rx="1.5"/><path d="M8 14V10l4 4V10"/><circle cx="17" cy="10" r="0.6" fill="currentColor"/><circle cx="17" cy="14" r="0.6" fill="currentColor"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['NTD', 'NBN', 'modem'],
  },
];

// ---------------------------------------------------------------------------
// Security (7)
// ---------------------------------------------------------------------------

const SECURITY: SymbolDefinition[] = [
  {
    id: 'dome-camera',
    label: 'Dome Camera',
    layer: 'security',
    svg: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="1" fill="currentColor"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['dome', 'IP camera', 'G5 dome'],
  },
  {
    id: 'bullet-camera',
    label: 'Bullet Camera',
    layer: 'security',
    // Cylinder with lens at front + mount stub.
    svg: '<rect x="4" y="9" width="14" height="6" rx="1"/><circle cx="7" cy="12" r="1.5"/><path d="M18 11l3-1v4l-3-1"/><path d="M11 15v3"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['bullet', 'IP camera', 'G5 bullet'],
  },
  {
    id: 'nvr-rack',
    label: 'NVR / Rack Recorder',
    layer: 'security',
    svg: '<rect x="3" y="7" width="18" height="10" rx="1"/><circle cx="7" cy="12" r="1"/><path d="M11 11h7M11 13h5"/>',
    defaultCatalogHints: ['NVR', 'recorder', 'CK-Enterprise'],
  },
  {
    id: 'doorbell',
    label: 'Video Doorbell',
    layer: 'security',
    svg: '<rect x="7" y="3" width="10" height="18" rx="2"/><circle cx="12" cy="9" r="2"/><circle cx="12" cy="16" r="1.2"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['doorbell', 'G4 doorbell', 'video doorbell'],
  },
  {
    id: 'intercom-panel',
    label: 'Intercom Panel',
    layer: 'security',
    // Wall plate with screen + button grid.
    svg: '<rect x="5" y="3" width="14" height="18" rx="1.5"/><rect x="7" y="6" width="10" height="5" rx="0.5"/><circle cx="9" cy="14" r="0.8"/><circle cx="12" cy="14" r="0.8"/><circle cx="15" cy="14" r="0.8"/><circle cx="9" cy="17" r="0.8"/><circle cx="12" cy="17" r="0.8"/><circle cx="15" cy="17" r="0.8"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['intercom', '2N', 'access panel'],
  },
  {
    id: 'motion-sensor',
    label: 'Motion Sensor',
    layer: 'security',
    // PIR sensor — pyramid shape with detection arcs.
    svg: '<rect x="8" y="4" width="8" height="10" rx="1"/><circle cx="12" cy="9" r="2"/><path d="M5 18q3-3 7-3t7 3"/><path d="M3 21q4-4 9-4t9 4"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['PIR', 'motion', 'sensor'],
  },
  {
    id: 'door-contact',
    label: 'Door Contact',
    layer: 'security',
    // Two pill-shaped halves with a gap (reed switch).
    svg: '<rect x="3" y="9" width="8" height="6" rx="3"/><rect x="13" y="9" width="8" height="6" rx="3"/>',
    defaultCatalogHints: ['reed', 'door sensor', 'magnetic contact'],
  },
];

// ---------------------------------------------------------------------------
// Smart Home (5)
// ---------------------------------------------------------------------------

const SMART_HOME: SymbolDefinition[] = [
  {
    id: 'smart-switch',
    label: 'Smart Switch',
    layer: 'smart_home',
    // Wall plate with single rocker.
    svg: '<rect x="5" y="3" width="14" height="18" rx="2"/><rect x="9" y="8" width="6" height="8" rx="1"/><path d="M12 11v2"/>',
    defaultCatalogHints: ['smart switch', 'wall plate', 'shelly'],
  },
  {
    id: 'dimmer',
    label: 'Dimmer',
    layer: 'smart_home',
    // Wall plate with rotary dial.
    svg: '<rect x="5" y="3" width="14" height="18" rx="2"/><circle cx="12" cy="12" r="3.5"/><path d="M12 8.5v3.5l2 1.5"/>',
    defaultCatalogHints: ['dimmer', 'rotary', 'smart dimmer'],
  },
  {
    id: 'relay-module',
    label: 'Relay Module',
    layer: 'smart_home',
    // Small box with terminal pads.
    svg: '<rect x="4" y="6" width="16" height="12" rx="1"/><path d="M7 9v6M11 9v6M15 9v6M19 12h-3"/>',
    defaultCatalogHints: ['relay', 'shelly 1', 'module'],
  },
  {
    id: 'smart-lock',
    label: 'Smart Lock',
    layer: 'smart_home',
    // Padlock + keypad.
    svg: '<path d="M8 11V8a4 4 0 0 1 8 0v3"/><rect x="5" y="11" width="14" height="10" rx="2"/><circle cx="12" cy="16" r="1.2"/>',
    defaultCatalogHints: ['smart lock', 'August', 'Yale'],
  },
  {
    id: 'thermostat',
    label: 'Thermostat',
    layer: 'smart_home',
    // Round dial with temperature mark.
    svg: '<circle cx="12" cy="12" r="8"/><path d="M12 6v3M12 17q2-2 0-5"/>',
    defaultCatalogHints: ['thermostat', 'Nest', 'Ecobee'],
  },
  // Crestron Cameo keypad family. One symbol per button-count variant
  // so the picker offers the actual placement choice. Engraving labels
  // are captured per-button on the annotation via PlanAnnotation.keypad
  // (free text, edited inline in AnnotationDetailPanel). The SVGs render
  // a vertical wall plate with N stacked button bars + LED dots — the
  // shape lighting installers recognise on shop drawings.
  {
    id: 'cameo-1',
    label: 'Cameo 1-btn',
    layer: 'smart_home',
    svg: '<rect x="6" y="3" width="12" height="18" rx="2"/><rect x="8" y="9" width="8" height="6" rx="1"/><circle cx="12" cy="12" r="0.8" fill="currentColor"/>',
    defaultCatalogHints: ['Crestron', 'Cameo', 'keypad'],
  },
  {
    id: 'cameo-2',
    label: 'Cameo 2-btn',
    layer: 'smart_home',
    svg: '<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M7 12h10"/><circle cx="12" cy="7.5" r="0.7" fill="currentColor"/><circle cx="12" cy="16.5" r="0.7" fill="currentColor"/>',
    defaultCatalogHints: ['Crestron', 'Cameo', 'keypad'],
  },
  {
    id: 'cameo-3',
    label: 'Cameo 3-btn',
    layer: 'smart_home',
    svg: '<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M7 9h10M7 15h10"/><circle cx="12" cy="6" r="0.6" fill="currentColor"/><circle cx="12" cy="12" r="0.6" fill="currentColor"/><circle cx="12" cy="18" r="0.6" fill="currentColor"/>',
    defaultCatalogHints: ['Crestron', 'Cameo', 'keypad'],
  },
  {
    id: 'cameo-4',
    label: 'Cameo 4-btn',
    layer: 'smart_home',
    svg: '<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M7 7.5h10M7 12h10M7 16.5h10"/><circle cx="12" cy="5.25" r="0.55" fill="currentColor"/><circle cx="12" cy="9.75" r="0.55" fill="currentColor"/><circle cx="12" cy="14.25" r="0.55" fill="currentColor"/><circle cx="12" cy="18.75" r="0.55" fill="currentColor"/>',
    defaultCatalogHints: ['Crestron', 'Cameo', 'keypad'],
  },
  {
    id: 'cameo-6',
    label: 'Cameo 6-btn',
    layer: 'smart_home',
    svg: '<rect x="6" y="3" width="12" height="18" rx="2"/><path d="M7 6h10M7 9h10M7 12h10M7 15h10M7 18h10"/><circle cx="12" cy="4.5" r="0.45" fill="currentColor"/><circle cx="12" cy="7.5" r="0.45" fill="currentColor"/><circle cx="12" cy="10.5" r="0.45" fill="currentColor"/><circle cx="12" cy="13.5" r="0.45" fill="currentColor"/><circle cx="12" cy="16.5" r="0.45" fill="currentColor"/><circle cx="12" cy="19.5" r="0.45" fill="currentColor"/>',
    defaultCatalogHints: ['Crestron', 'Cameo', 'keypad'],
  },
  // Crestron Horizon keypad family — the newer flat-glass / capacitive
  // line. Rendered with a slightly wider, more rounded outer plate
  // (rx=3 vs Cameo's rx=2) and small horizontal dashes on the right
  // edge as capacitive touch indicators (vs Cameo's center LED dots).
  // The 2 styles read distinctly at picker thumbnail size — installers
  // can tell at a glance whether a plan calls for Cameo or Horizon.
  {
    id: 'horizon-2',
    label: 'Horizon 2-btn',
    layer: 'smart_home',
    svg: '<rect x="5" y="3" width="14" height="18" rx="3"/><path d="M14 7.5h2M14 16.5h2"/>',
    defaultCatalogHints: ['Crestron', 'Horizon', 'keypad'],
  },
  {
    id: 'horizon-4',
    label: 'Horizon 4-btn',
    layer: 'smart_home',
    svg: '<rect x="5" y="3" width="14" height="18" rx="3"/><path d="M14 6h2M14 10.5h2M14 15h2M14 19.5h2"/>',
    defaultCatalogHints: ['Crestron', 'Horizon', 'keypad'],
  },
  {
    id: 'horizon-6',
    label: 'Horizon 6-btn',
    layer: 'smart_home',
    svg: '<rect x="5" y="3" width="14" height="18" rx="3"/><path d="M14 5h2M14 8h2M14 11h2M14 14h2M14 17h2M14 20h2"/>',
    defaultCatalogHints: ['Crestron', 'Horizon', 'keypad'],
  },
  {
    id: 'horizon-8',
    label: 'Horizon 8-btn',
    layer: 'smart_home',
    svg: '<rect x="5" y="3" width="14" height="18" rx="3"/><path d="M14 4.5h2M14 6.75h2M14 9h2M14 11.25h2M14 13.5h2M14 15.75h2M14 18h2M14 20.25h2"/>',
    defaultCatalogHints: ['Crestron', 'Horizon', 'keypad'],
  },
];

// ---------------------------------------------------------------------------
// AV (4)
// ---------------------------------------------------------------------------

const AV: SymbolDefinition[] = [
  {
    id: 'tv',
    label: 'TV',
    layer: 'av',
    svg: '<rect x="2" y="5" width="20" height="12" rx="1"/><path d="M9 21h6M12 17v4"/>',
    defaultCableType: 'HDMI',
    defaultCatalogHints: ['TV', 'display', 'panel'],
  },
  {
    id: 'speaker-ceiling',
    label: 'Ceiling Speaker',
    layer: 'av',
    svg: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1" fill="currentColor"/>',
    defaultCableType: '14/2 Speaker',
    defaultCatalogHints: ['speaker', 'in-ceiling', 'JBL'],
  },
  {
    id: 'speaker-wall',
    label: 'Wall Speaker',
    layer: 'av',
    // Trapezoid (front grille view) with horizontal grille lines + small driver dot.
    svg: '<path d="M5 4h14l-2 16H7z"/><path d="M7 9h10M7 13h10M7 17h10"/><circle cx="12" cy="11" r="1" fill="currentColor"/>',
    defaultCableType: '14/2 Speaker',
    defaultCatalogHints: ['speaker', 'wall mount', 'surface mount', 'JBL Control'],
  },
  {
    id: 'speaker-pendant',
    label: 'Pendant Speaker',
    layer: 'av',
    // Suspension line from top + bell-shaped pendant body with driver.
    svg: '<path d="M12 2v4"/><path d="M7 7h10l-2 13H9z"/><circle cx="12" cy="14" r="3"/><circle cx="12" cy="14" r="1" fill="currentColor"/>',
    defaultCableType: '16/2 Speaker',
    defaultCatalogHints: ['pendant', 'hanging speaker', 'JBL Control 67'],
  },
  {
    id: 'speaker-outdoor',
    label: 'Outdoor Speaker',
    layer: 'av',
    // Rounded box with weather-sealed grille (concentric arcs) + drainage notch.
    svg: '<rect x="4" y="5" width="16" height="14" rx="3"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/><path d="M10 19v1.5h4V19"/>',
    defaultCableType: '14/2 Direct-Burial Speaker',
    defaultCatalogHints: ['outdoor', 'weatherproof', 'landscape speaker', 'IP rated'],
  },
  {
    id: 'subwoofer',
    label: 'Subwoofer',
    layer: 'av',
    // Box with large central woofer + small port.
    svg: '<rect x="3" y="4" width="18" height="16" rx="1"/><circle cx="12" cy="13" r="4.5"/><circle cx="12" cy="13" r="1.5" fill="currentColor"/><circle cx="6" cy="7" r="0.6" fill="currentColor"/>',
    defaultCableType: '12/2 Speaker',
    defaultCatalogHints: ['subwoofer', 'sub', 'in-wall sub'],
  },
  {
    id: 'projector',
    label: 'Projector',
    layer: 'av',
    // Body + lens cone.
    svg: '<rect x="2" y="8" width="14" height="10" rx="1"/><circle cx="6" cy="13" r="2"/><path d="M16 11l5-2v8l-5-2"/>',
    defaultCableType: 'HDMI',
    defaultCatalogHints: ['projector', 'epson', 'BenQ'],
  },
];

// ---------------------------------------------------------------------------
// Power / Structured (5 — split mostly evenly across the two layers)
// ---------------------------------------------------------------------------

const POWER_STRUCTURED: SymbolDefinition[] = [
  {
    id: 'gpo',
    label: 'Power Outlet (GPO)',
    layer: 'power',
    // Australian-style 3-prong outline.
    svg: '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9l-1.5 3M15 9l1.5 3M12 14v3"/>',
    defaultCatalogHints: ['GPO', 'outlet', 'powerpoint'],
  },
  {
    id: 'data-wall-plate',
    label: 'Data Wall Plate',
    layer: 'structured',
    // Wall plate with two RJ45 keystones.
    svg: '<rect x="5" y="4" width="14" height="16" rx="1.5"/><rect x="8" y="8" width="8" height="3" rx="0.5"/><rect x="8" y="13" width="8" height="3" rx="0.5"/>',
    defaultCableType: 'Cat6',
    defaultCatalogHints: ['wall plate', 'keystone', 'RJ45'],
  },
  {
    id: 'rack',
    label: 'Equipment Rack',
    layer: 'structured',
    // Rack with horizontal rails.
    svg: '<rect x="5" y="3" width="14" height="18" rx="0.5"/><path d="M5 8h14M5 12h14M5 16h14M9 5v0M11 5v0"/>',
    defaultCatalogHints: ['rack', 'enclosure', '12U', '24U'],
  },
  {
    id: 'ups',
    label: 'UPS',
    layer: 'power',
    // Battery icon with charge bars.
    svg: '<rect x="4" y="6" width="15" height="12" rx="1"/><path d="M19 10v4h2v-4z"/><path d="M7 9v6M10 9v6M13 9v6"/>',
    defaultCatalogHints: ['UPS', 'battery backup', 'CyberPower'],
  },
  {
    id: 'conduit-entry',
    label: 'Conduit Entry',
    layer: 'structured',
    // Pipe entering wall — circle with diagonal pipe.
    svg: '<circle cx="9" cy="15" r="4"/><path d="M11.8 12.2l8-8M16 4h4v4"/>',
    defaultCatalogHints: ['conduit', '20mm', 'pipe'],
  },
];

// ---------------------------------------------------------------------------
// SYMBOLS — flat ordered list. Picker (P4a.8) groups via `layer`.
// ---------------------------------------------------------------------------

export const SYMBOLS: SymbolDefinition[] = [
  ...NETWORKING,
  ...SECURITY,
  ...SMART_HOME,
  ...AV,
  ...POWER_STRUCTURED,
];

const SYMBOL_BY_ID = new Map(SYMBOLS.map((s) => [s.id, s]));

// ---------------------------------------------------------------------------
// User-created symbols
// ---------------------------------------------------------------------------
//
// Symbols the tenant has built for themselves live in Firestore, so they
// cannot be part of the module-level catalogue above. They are published into
// this overlay by `useCustomSymbols` as soon as they load.
//
// The overlay exists so that `getSymbol()` stays THE way to resolve a symbol.
// Eighteen call sites depend on it — the canvas, the read-only renderer, the
// PDF export, the cost roll-up, the diff view, the auto-linker — and every one
// of them keeps working unchanged. The alternative (threading a symbol map
// through all of them) would have been a far bigger change with far more
// places to forget.
//
// Reads are synchronous and may run before the subscription has delivered, so
// a placed custom symbol can miss its definition for the first frame or two.
// Callers already treat an unresolved id as "draw the generic point", which is
// the right fallback here too.

let CUSTOM_SYMBOLS: SymbolDefinition[] = [];
let CUSTOM_BY_ID = new Map<string, SymbolDefinition>();

/** Replace the user-created symbol overlay. Called by `useCustomSymbols` on
 *  every Firestore snapshot; not for general use. */
export function setCustomSymbols(symbols: SymbolDefinition[]): void {
  CUSTOM_SYMBOLS = symbols.map((s) => ({ ...s, custom: true }));
  CUSTOM_BY_ID = new Map(CUSTOM_SYMBOLS.map((s) => [s.id, s]));
}

/** The user-created symbols currently loaded. */
export function getCustomSymbols(): SymbolDefinition[] {
  return CUSTOM_SYMBOLS;
}

/** Resolve a symbol definition by id. Returns undefined if the id is
 *  unknown — caller should treat that as a placement-time error.
 *
 *  User-created symbols win over built-ins on an id clash. That is the
 *  friendlier default (someone who names a symbol over a built-in meant to
 *  replace it), and the create path prefixes custom ids anyway so it should
 *  never come up. */
export function getSymbol(id: string): SymbolDefinition | undefined {
  return CUSTOM_BY_ID.get(id) ?? SYMBOL_BY_ID.get(id);
}

/** Built-ins plus everything the user has made. Pickers want this;
 *  `SYMBOLS` alone silently hides custom symbols. */
export function getAllSymbols(): SymbolDefinition[] {
  return [...SYMBOLS, ...CUSTOM_SYMBOLS];
}

/** Filter to symbols on a specific layer. Used by the P4a.8 picker
 *  when the user toggles layer chips. */
export function getSymbolsByLayer(layer: SymbolLayer): SymbolDefinition[] {
  return getAllSymbols().filter((s) => s.layer === layer);
}

/**
 * The stroke colour a symbol should be drawn in: its own colour if it has one
 * (user-created symbols only), otherwise the layer accent.
 *
 * Every surface that draws an annotation must go through this, or a custom
 * symbol comes out the right colour on the canvas and the wrong one in the PDF.
 */
export function resolveSymbolHex(
  symbolId: string | null | undefined,
  layerHex: string
): string {
  const custom = symbolId ? getSymbol(symbolId)?.color : undefined;
  return custom || layerHex;
}

/** Symbol IDs that represent speakers. Drives the AnnotationDetailPanel
 *  decision to surface the speaker-zone editor + the FloorplanCanvas
 *  decision to apply the per-zone colour override. Adding a new speaker
 *  variant = one entry to AV[] above + one id here. */
export const SPEAKER_SYMBOL_IDS: ReadonlySet<string> = new Set([
  'speaker-ceiling',
  'speaker-wall',
  'speaker-pendant',
  'speaker-outdoor',
  'subwoofer',
]);

/** Convenience predicate. Returns true when the symbol id resolves to
 *  a speaker variant. Safe to call with undefined. */
export function isSpeakerSymbol(symbolId: string | undefined | null): boolean {
  return !!symbolId && SPEAKER_SYMBOL_IDS.has(symbolId);
}
